// import { randCatchPhrase } from "@ngneat/falso";

import type {
  SingleRecentHeyConversationDetails,
  SingleRecentHeyConversationDetailsWithParsedMessages,
} from "@/types/v2";
import type { QueryClient } from "@tanstack/vue-query";
import type { MaybeRef } from "vue";

type MessageSender = SingleRecentHeyConversationDetails["messages"][number]["role"];

export function useV2SingleConversationDetailsRequest(conversationId: MaybeRef<string>) {
  const enabled = computed(() => !!unref(conversationId));
  const request = useQuery({
    queryKey: ["v2", "conversations", "messages", conversationId],
    queryFn: async () => queryFn(unref(conversationId)),
    staleTime: 3 * MINUTE,
    enabled,
  });
  return request;
}

//
// UTILS
//

async function queryFn(conversationId: string) {
  const response = await useGetRequest<SingleRecentHeyConversationDetails>(`/hey`, {
    params: {
      id: conversationId,
    },
  });
  const data = response?.data || null;
  if (!data) return null;
  const parsedData = data.messages.map(msg => ({
    ...msg,
    content: formatV2ResponseMessage(msg.content),
  }));
  return { ...data, messages: parsedData };
}

export function getCachedConversationDetailsById(id: string, queryClient: QueryClient) {
  return queryClient.getQueryData<SingleRecentHeyConversationDetails>([
    "v2",
    "conversations",
    "messages",
    id,
  ]);
}

export function prefetchSingleMessageDetails(
  conversationId: string,
  queryClient: QueryClient
) {
  return queryClient.prefetchQuery({
    queryKey: ["v2", "conversations", "messages", conversationId],
    queryFn: async () => queryFn(conversationId),
  });
}

export function addMessageToConversationDetails(
  conversationId: string,
  message: { role: MessageSender; content: string; created: string },
  queryClient: QueryClient
) {
  if (!conversationId) return;
  const existingConversation =
    queryClient.getQueryData<SingleRecentHeyConversationDetailsWithParsedMessages>([
      "v2",
      "conversations",
      "messages",
      conversationId,
    ]);
  if (!existingConversation) return;
  const id = getRandomId();
  const parsedMessage = {
    ...message,
    content: formatV2ResponseMessage(message.content),
  };
  const newMessages = [...existingConversation.messages, { id, ...parsedMessage }];
  queryClient.setQueryData<SingleRecentHeyConversationDetailsWithParsedMessages>(
    ["v2", "conversations", "messages", conversationId],
    {
      ...existingConversation,
      messages: newMessages,
    }
  );
}

export function removeLastMessageFromConversationDetails(
  conversationId: string,
  queryClient: QueryClient
) {
  if (!conversationId) return null;
  const existingConversation =
    queryClient.getQueryData<SingleRecentHeyConversationDetailsWithParsedMessages>([
      "v2",
      "conversations",
      "messages",
      conversationId,
    ]);
  if (!existingConversation?.messages) return null;
  const lastMessage = existingConversation.messages.slice(-1)[0];
  const newMessages = existingConversation.messages.slice(0, -1);
  queryClient.setQueryData<SingleRecentHeyConversationDetailsWithParsedMessages>(
    ["v2", "conversations", "messages", conversationId],
    {
      ...existingConversation,
      messages: newMessages,
    }
  );
  return lastMessage;
}
